import { deleteFile } from 'src/shared/services/indexedDBService';
import {
  PERSISTED_UPLOADS_KEY,
  PERSISTED_UPLOAD_REMOVED_EVENT,
  PersistedUpload,
} from 'src/pages/media-library/context/media-upload-context';
import { Logger } from 'src/utils/logger';

const readPersistedUploads = (): PersistedUpload[] => {
  try {
    const raw = localStorage.getItem(PERSISTED_UPLOADS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writePersistedUploads = (uploads: PersistedUpload[]) => {
  try {
    localStorage.setItem(PERSISTED_UPLOADS_KEY, JSON.stringify(uploads));
  } catch (err) {
    Logger.warn(
      'Failed to persist updated upload list to localStorage',
      'persisted-uploads-storage',
      { err },
    );
  }
};

/**
 * Remove a persisted upload from localStorage AND drop its file from
 * IndexedDB. Safe to call when MediaUploadProvider is not mounted.
 *
 * Dispatches PERSISTED_UPLOAD_REMOVED_EVENT after writing localStorage so any
 * mounted MediaUploadProvider can drop the entry from its in-memory mirror.
 * Without that bridge, the provider's setPersistedUploads-on-progress effect
 * would re-write the dismissed metadata back to localStorage on the next
 * progress tick from a concurrent upload — the IndexedDB blob is already
 * gone but the orphan metadata would survive.
 */
const removePersistedUploadStorage = async (id: string): Promise<void> => {
  const next = readPersistedUploads().filter((u) => u.id !== id);
  writePersistedUploads(next);
  // Fire-and-forget: if no MediaUploadProvider is mounted (e.g. post-signout,
  // or in a tab where the provider unmounted) the event is silently dropped
  // and the localStorage write stands alone — that's the desired behavior
  // since there's no in-memory mirror that needs syncing.
  if (typeof window !== 'undefined') {
    window.dispatchEvent(
      new CustomEvent(PERSISTED_UPLOAD_REMOVED_EVENT, { detail: { id } }),
    );
  }
  if (id) await deleteFile(id);
};

export { readPersistedUploads, removePersistedUploadStorage };
